/**
 * Raport zarządu — definicje wskaźników do podpowiedzi kafli (`mbr.common.def.<sekcja>.<klucz>`, klucz = MbrKpi.key).
 * Plik FUNDAMENTU. Nowy kafel sekcji: dopisz definicję tutaj (brak definicji = kafel bez ikony „i”, nie błąd).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrDefsPl = {
  sales: {
    netSales: 'Sprzedaż netto z faktur minus korekty w miesiącu (bez VAT).',
    orders: 'Liczba dokumentów sprzedaży (zamówień dostarczonych) w miesiącu.',
    ordersPerDeliveryDay: 'Zamówienia podzielone przez liczbę dni dostaw z kalendarza tras.',
    grossMarginPct: 'Zysk ze sprzedaży jako % sprzedaży po korektach (sprzedaż − koszt towaru).',
    profitPerOrder: 'Zysk ze sprzedaży podzielony przez liczbę zamówień.',
    cogs: 'Koszt zakupu sprzedanego towaru (wg cen zakupu z ERP).',
    grossProfit: 'Sprzedaż po korektach minus koszt towaru.',
    corrections: 'Wartość faktur korygujących w miesiącu.',
    correctionsPct: 'Korekty jako % sprzedaży przed korektami.',
    operatingResult: 'Zysk ze sprzedaży minus koszty stałe, zmienne i wynagrodzenia z zamknięcia miesiąca.',
    distanceToBep:
      'Ile sprzedaży brakuje do progu rentowności (ujemne) albo o ile go przekroczono (dodatnie).',
    contributionMarginPct: 'Marża po kosztach zmiennych jako % sprzedaży — podstawa progu rentowności.',
    bep: 'Sprzedaż, przy której wynik operacyjny = 0: koszty stałe / marża kontrybucji.',
    pctAboveBep: 'Sprzedaż jako % progu rentowności (100% = wynik zero).',
  },
  customers: {
    ordering: 'Klienci z co najmniej jedną fakturą w miesiącu.',
    gained: 'Nowi klienci i odzyskani po przerwie dłuższej niż próg utraty z ustawień.',
    lost: 'Klienci, którzy zamawiali wcześniej, a od progu z ustawień nie zamawiają (bez sezonowych).',
    balanceMargin: 'Zysk brutto pozyskanych minus zysk brutto utraconych (z okresu bazowego).',
    balanceAmount: 'Obrót pozyskanych minus obrót utraconych.',
    gainedAmount: 'Sprzedaż do klientów pozyskanych w miesiącu.',
    lostAmount: 'Sprzedaż utraconych klientów w ich ostatnim okresie zamawiania.',
  },
  logistics: {
    fuelCost: 'Koszt paliwa aut dystrybucji (karty paliwowe i wydatki floty).',
    km: 'Przejechane kilometry aut dystrybucji (z kilometrówek i tras).',
    kmPerOrder: 'Kilometry podzielone przez liczbę zamówień.',
    hoursPerOrder: 'Godziny pracy kierowców podzielone przez liczbę zamówień.',
    costPerKm: 'Paliwo + najem + nadprzebieg podzielone przez kilometry.',
    driversOverLimit: 'Kierowcy, którzy przekroczyli miesięczny próg godzin z ustawień.',
    driverHours: 'Suma godzin pracy kierowców w miesiącu.',
    hoursPerDeliveryDay: 'Godziny kierowców podzielone przez liczbę dni dostaw.',
    fuelPerKm: 'Koszt paliwa podzielony przez kilometry.',
    rentCost: 'Raty najmu i leasingu aut w miesiącu.',
    excessMileageCost: 'Naliczenie za przebieg ponad limit umowy najmu.',
    vehiclesRented: 'Auta dystrybucji objęte umową najmu lub leasingu.',
    vehiclesNotRented: 'Auta dystrybucji własne (bez umowy najmu).',
  },
  operations: {
    complaints: 'Liczba reklamacji zarejestrowanych w miesiącu.',
    complaintsPctOfOrders: 'Reklamacje jako % zamówień.',
    complaintsPerDeliveryDay: 'Reklamacje podzielone przez liczbę dni dostaw.',
    complaintsValue: 'Wartość reklamowanych pozycji.',
    disposalValue: 'Wartość towaru zutylizowanego w miesiącu.',
    disposalPctOfSales: 'Utylizacja jako % sprzedaży po korektach.',
    pickingMinutesPerOrder: 'Minuty kompletacji podzielone przez liczbę zamówień.',
    orders: 'Zamówienia skompletowane w magazynie.',
    ordersPerDeliveryDay: 'Zamówienia magazynu podzielone przez dni dostaw.',
    pickingHours: 'Suma godzin pracy kompletacji.',
    pickingHoursPerDeliveryDay: 'Godziny kompletacji podzielone przez dni dostaw.',
    orderLines: 'Liczba pozycji (linii) w zamówieniach.',
    pickingMinutesPerLine: 'Minuty kompletacji podzielone przez liczbę pozycji.',
    linesPerOrder: 'Średnia liczba pozycji w zamówieniu.',
    inventoryBalance: 'Saldo inwentaryzacji: nadwyżki minus niedobory.',
    productionSales: 'Sprzedaż wyrobów z własnej produkcji.',
    productionCost: 'Koszt wytworzenia wyrobów z własnej produkcji.',
  },
  finance: {
    fixedCosts: 'Koszty stałe miesiąca bez wynagrodzeń (czynsze, media, usługi).',
    variableCosts: 'Koszty zmienne miesiąca bez wynagrodzeń (zależne od sprzedaży).',
    payroll: 'Koszt wynagrodzeń z formularza kadr.',
    payrollPerSalesPln: 'Wynagrodzenia podzielone przez sprzedaż po korektach.',
    receivablesOverdue: 'Należności po terminie płatności na koniec miesiąca.',
    collectionsAmount: 'Należności przekazane do kancelarii windykacyjnej.',
    totalCosts: 'Koszty stałe + zmienne + wynagrodzenia.',
    headcount: 'Liczba osób zatrudnionych na koniec miesiąca.',
    employerCost: 'Całkowity koszt pracodawcy z danych kadrowych.',
    erpOverdue: 'Przeterminowane należności według rozrachunków w Subiekcie.',
    erpCei: 'Skuteczność windykacji: część należności do odzyskania, którą odzyskano w miesiącu.',
  },
};

export const mbrDefsEn: Shape<typeof mbrDefsPl> = {
  sales: {
    netSales: 'Net invoiced sales minus corrections in the month (excl. VAT).',
    orders: 'Number of sales documents (delivered orders) in the month.',
    ordersPerDeliveryDay: 'Orders divided by the delivery days from the route calendar.',
    grossMarginPct: 'Gross profit as % of net sales (sales − cost of goods).',
    profitPerOrder: 'Gross profit divided by the number of orders.',
    cogs: 'Purchase cost of the goods sold (ERP purchase prices).',
    grossProfit: 'Net sales minus cost of goods.',
    corrections: 'Value of correction invoices in the month.',
    correctionsPct: 'Corrections as % of sales before corrections.',
    operatingResult: 'Gross profit minus fixed, variable and payroll costs from the month close.',
    distanceToBep: 'Sales missing to break-even (negative) or above it (positive).',
    contributionMarginPct: 'Margin after variable costs as % of sales — the basis of break-even.',
    bep: 'Sales at which the operating result is 0: fixed costs / contribution margin.',
    pctAboveBep: 'Sales as % of break-even (100% = zero result).',
  },
  customers: {
    ordering: 'Customers with at least one invoice in the month.',
    gained: 'New customers and those won back after a gap longer than the loss threshold.',
    lost: 'Customers who used to order and stopped beyond the threshold (seasonal ones excluded).',
    balanceMargin: 'Gross profit of gained minus gross profit of lost customers (base period).',
    balanceAmount: 'Turnover of gained minus turnover of lost customers.',
    gainedAmount: 'Sales to customers gained in the month.',
    lostAmount: 'Sales of lost customers in their last ordering period.',
  },
  logistics: {
    fuelCost: 'Fuel cost of distribution vehicles (fuel cards and fleet expenses).',
    km: 'Kilometres driven by distribution vehicles (logbooks and routes).',
    kmPerOrder: 'Kilometres divided by orders.',
    hoursPerOrder: 'Driver hours divided by orders.',
    costPerKm: 'Fuel + rent + excess mileage divided by kilometres.',
    driversOverLimit: 'Drivers above the monthly hours threshold from the settings.',
    driverHours: 'Total driver working hours in the month.',
    hoursPerDeliveryDay: 'Driver hours divided by delivery days.',
    fuelPerKm: 'Fuel cost divided by kilometres.',
    rentCost: 'Vehicle rent and lease instalments in the month.',
    excessMileageCost: 'Charge for mileage above the rental contract limit.',
    vehiclesRented: 'Distribution vehicles under a rent or lease contract.',
    vehiclesNotRented: 'Owned distribution vehicles (no rental contract).',
  },
  operations: {
    complaints: 'Complaints registered in the month.',
    complaintsPctOfOrders: 'Complaints as % of orders.',
    complaintsPerDeliveryDay: 'Complaints divided by delivery days.',
    complaintsValue: 'Value of the complained items.',
    disposalValue: 'Value of goods disposed of in the month.',
    disposalPctOfSales: 'Disposal as % of net sales.',
    pickingMinutesPerOrder: 'Picking minutes divided by orders.',
    orders: 'Orders picked in the warehouse.',
    ordersPerDeliveryDay: 'Warehouse orders divided by delivery days.',
    pickingHours: 'Total picking hours.',
    pickingHoursPerDeliveryDay: 'Picking hours divided by delivery days.',
    orderLines: 'Number of lines in orders.',
    pickingMinutesPerLine: 'Picking minutes divided by lines.',
    linesPerOrder: 'Average lines per order.',
    inventoryBalance: 'Stock-take balance: surpluses minus shortages.',
    productionSales: 'Sales of own-production goods.',
    productionCost: 'Cost of producing own goods.',
  },
  finance: {
    fixedCosts: 'Fixed costs of the month excluding payroll (rent, utilities, services).',
    variableCosts: 'Variable costs of the month excluding payroll (sales-driven).',
    payroll: 'Payroll cost from the HR form.',
    payrollPerSalesPln: 'Payroll divided by net sales.',
    receivablesOverdue: 'Receivables past due at month end.',
    collectionsAmount: 'Receivables handed over to the debt-collection firm.',
    totalCosts: 'Fixed + variable + payroll costs.',
    headcount: 'Employees at month end.',
    employerCost: 'Total employer cost from HR data.',
    erpOverdue: 'Overdue receivables per the Subiekt ledger.',
    erpCei: 'Collection effectiveness: share of collectible receivables collected in the month.',
  },
};
